import {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import { Box, Stack, TextField } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button, ToastType } from '@components';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import {
  editRemitEmailApi,
  exportRemitToQueueApi,
  fetchRemitToQueueApi,
} from '@store/apCallingQueue';
import { createAppStore } from '@store/createAppStore';
import {
  dateComparator,
  dateFormatter,
  phoneNumberCellFormatter,
} from '@store/loadboard/utils';
import { closeModal, openModal } from '@store/modals';
import { RemitToQueueType } from '@typeDefinitions/apCallingQueueTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { handleToast, validEmail } from '@utils/utils';
import { downloadCsv } from '@views/efsHistory/constants';
import { atgLogo } from '@views/ltl';
import { createSignal } from 'solid-js';

import { transformFilters } from './constants';

const columns: ColDef[] = [
  {
    headerName: 'Carrier Name',
    field: 'carrierName',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    flex: 2,
  },
  {
    headerName: 'MC',
    field: 'mc',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'DOT',
    field: 'dot',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Last Load Date',
    field: 'lastLoadDate',
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: dateComparator,
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    valueFormatter: dateFormatter,
    floatingFilter: true,
  },
  {
    headerName: 'Contact Name',
    field: 'contactName',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Type',
    field: 'type',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Phone',
    field: 'phone',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    valueFormatter: phoneNumberCellFormatter,
    cellRenderer: (params: ICellRendererParams) => {
      const value = params.value as string;
      return (
        <a
          href={`tel:${value}`}
          style={{
            'text-decoration': 'underline',
            'text-decoration-color': '#333c4d',
            color: '#333c4d',
          }}
        >
          {formatPhoneNumber(value)}
        </a>
      );
    },
  },
  {
    headerName: 'Ext',
    field: 'extension',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Email',
    field: 'email',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    cellRenderer: (params: ICellRendererParams) => {
      const value = params.value as string;
      return (
        <a
          href={`mailto:${value}`}
          style={{
            'text-decoration': 'underline',
            'text-decoration-color': '#333c4d',
            color: '#333c4d',
          }}
        >
          {value}
        </a>
      );
    },
    flex: 2,
  },
];

export const RemitToQueue = () => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [isLoading, setLoading] = createSignal(false);
  const [selectedRecord, setSelectedRecord] = createAppStore<RemitToQueueType>(
    {} as RemitToQueueType,
    'remitToQueueSelectedRecord',
    true,
  );

  const [exportLoading, setExportLoading] = createSignal<boolean>(false);
  const [gridPayload, setGridPayload] = createSignal<Record<string, unknown>>(
    {},
  );

  const handleExport = async () => {
    const filters = { ...gridPayload() };
    delete filters.pageNumber;
    delete filters.pageSize;
    setExportLoading(true);
    const csvData = await exportRemitToQueueApi(filters);
    setExportLoading(false);
    if (typeof csvData === 'string') {
      downloadCsv(csvData, 'RemitToQueue.csv');
    } else {
      handleToast(ToastType.Error, 'Invalid CSV data received');
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: RemitToQueueType[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: [...transformFilters(params.request.filterModel)],
          sort: {
            field: orderBy || 'carrierName',
            direction: sortBy || 'asc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 500,
        };
        setGridPayload(filteredObject);
        try {
          gridRef()?.api.showLoadingOverlay();
          const response: unknown = await fetchRemitToQueueApi(filteredObject);
          params.success({
            rowData: (response as { data: RemitToQueueType[] }).data,
            rowCount: (response as { totalCount: number }).totalCount,
          });
        } catch (error) {
          params.fail();
        } finally {
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  const handleSave = async () => {
    const id = selectedRecord.id;
    const email = selectedRecord.email.trim();
    if (!validEmail(email)) {
      handleToast(ToastType.Error, 'Input is not a valid email address.');
      return;
    }
    setLoading(true);
    const response = await editRemitEmailApi(id, email);
    setLoading(false);
    if (Boolean(response)) {
      gridRef()?.api.refreshServerSide({ purge: true });
      handleToast(ToastType.Success, 'Remittance email saved');
      closeModal('edit-remit-to');
    } else {
      handleToast(ToastType.Error, 'Remittance email not saved: ');
    }
  };

  return (
    <>
      <Stack class="h-full">
        <Box class="w-full p-2 border-gray-400 rounded-xs border-b-0 border bg-[#F8F8F8]">
          <Button
            label={
              <div class="flex items-center gap-1">
                <img src={ExcelSheetIcon} height="18px" alt="excelsheet" />
                Export to Excel
              </div>
            }
            variant="contained"
            size="sm"
            onClick={handleExport}
            disabled={exportLoading()}
            isLoading={exportLoading()}
            dataTestId={formatDataTestId('Export to Excel', 'button')}
          />
        </Box>
        <Box class="ag-theme-alpine flex flex-col flex-1! min-h-[400px]">
          <AgGridSolid
            ref={gridRef}
            overlayNoRowsTemplate="No data available"
            overlayLoadingTemplate={atgLogo}
            columnDefs={columns}
            getRowStyle={getRowGroupStyle}
            rowClass={'cursor-pointer'}
            rowHeight={40}
            gridOptions={{
              defaultColDef: {
                flex: 1,
                filter: true,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  'line-height': '20px',
                },
              },
              pagination: true,
              suppressPaginationPanel: false,
              maintainColumnOrder: true,
              paginationPageSizeSelector: false,
              sideBar: {
                toolPanels: [columnsToolPanel, filtersToolPanel],
              },
              rowModelType: 'serverSide',
              paginationPageSize: 500,
              cacheBlockSize: 500,
              onGridReady: onGridReady,
            }}
            onRowClicked={(e) => {
              const target = e.event?.target as HTMLElement;
              if (target.closest('a')) return;
              const record = e.data as RemitToQueueType;
              if (Boolean(record)) {
                setSelectedRecord(record);
                openModal('edit-remit-to');
              }
            }}
          />
        </Box>
      </Stack>
      <BasicModal id="edit-remit-to" title="Edit Remit To" footer={false}>
        <Stack spacing={2}>
          <TextField
            label="Carrier Name"
            disabled
            value={selectedRecord.carrierName}
            dataTestId={'carrier-name-textInput'}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="MC"
              disabled
              value={selectedRecord.mc}
              dataTestId={'mc-textInput'}
            />
            <TextField
              label="DOT"
              disabled
              value={selectedRecord.dot}
              dataTestId={'dot-textInput'}
            />
          </Stack>
          <TextField
            label="Remit To"
            dataTestId={'remit-to-textInput'}
            placeholder="Enter Remit To Email"
            value={selectedRecord.email}
            onChange={(e: string) => setSelectedRecord('email', e)}
            type="email"
          />
          <Button
            label="Save"
            onClick={handleSave}
            disabled={isLoading()}
            isLoading={isLoading()}
            dataTestId={formatDataTestId('Save', 'button')}
          />
        </Stack>
      </BasicModal>
    </>
  );
};
